/** @badge Custom — счётчик/лейбл-пилюля с glass-градиентом (число, иконка или текст)
 *  @when   бейдж-статус или счётчик рядом с заголовком: «forced», «focus», непрочитанное число
 *  @not    кликабельная сущность с фразой — это Chip · длинная подпись — это Cell.subtitle
 *  @breaks onClick — голый div без haptic и фокус-кольца, Tappable/Chip тут не подключён
 */
"use client";

import { ReactNode, useRef, useEffect } from "react";
import { Icon } from "./Icon";
import { AnimatedWrapper } from "./AnimatedWrapper";
import { accent, space, corners, typography, text } from "@/lib/tokens/semantic";
import { mix } from "@/lib/tokens/mix";

type BadgeVariant = "forced" | "focus" | "primary" | "success" | "info";
type BadgeSize = "sm" | "md" | "lg" | "xl" | "xxl";

interface BadgeProps {
  count?: number;
  icon?: string;
  label?: ReactNode;
  variant?: BadgeVariant;
  fill?: boolean;
  size?: BadgeSize;
  onClick?: () => void;
}

const pillGlass = {
  forced: {
    bg: `linear-gradient(135deg, ${mix(accent.forced, 15)} 0%, ${mix(accent.forced, 8)} 100%)`,
    border: mix(accent.forced, 25),
    shadow: `0 1px 4px ${mix(accent.forced, 12)}`,
    color: accent.forced,
  },
  focus: {
    bg: `linear-gradient(135deg, ${accent.secondary} 0%, ${mix(accent.secondary, 87)} 100%)`,
    border: accent.secondary,
    shadow: `0 2px 8px ${mix(accent.secondary, 25)}`,
    color: text.onAccent,
  },
} as const;

const flatVariants: Record<string, { bg: string; color: string; iconColor: "forced" | "accent" | "success" | "default" }> = {
  primary: { bg: accent.primaryMuted, color: accent.primary, iconColor: "accent" },
  success: { bg: accent.successMuted, color: accent.success, iconColor: "success" },
  info: { bg: accent.infoMuted, color: accent.info, iconColor: "accent" },
};

export function Badge({ count, icon, label, variant = "primary", fill = false, size = "sm", onClick }: BadgeProps) {
  const show = !(count === 0 && !label);
  const lastCount = useRef(count);
  
  useEffect(() => {
    if (count && count > 0) lastCount.current = count;
  }, [count]);
  
  const displayCount = show ? count : lastCount.current;
  const isPill = variant === "forced" || variant === "focus";
  
  const base = {
    display: "inline-flex",
    alignItems: "center" as const,
    gap: space.insetSm,
    padding: `${space.insetSm} ${space.insetMd}`,
    cursor: onClick ? "pointer" : undefined,
  };
  
  const style = isPill ? {
    ...base,
    background: pillGlass[variant as "forced" | "focus"].bg,
    borderRadius: corners.pill,
    border: `1px solid ${pillGlass[variant as "forced" | "focus"].border}`,
    boxShadow: pillGlass[variant as "forced" | "focus"].shadow,
    backdropFilter: "blur(8px)",
  } : {
    ...base,
    background: (flatVariants[variant] || flatVariants.primary).bg,
    borderRadius: corners.button,
  };
  
  const color = isPill ? pillGlass[variant as "forced" | "focus"].color : (flatVariants[variant] || flatVariants.primary).color;
  const iconColor = isPill ? (variant === "forced" ? "forced" : "default") : (flatVariants[variant] || flatVariants.primary).iconColor;
  
  return (
    <AnimatedWrapper show={show} style={style}>
      <div onClick={onClick} style={{ display: "flex", alignItems: "center", gap: space.insetSm }}>
        {icon && <Icon name={icon} size={size} color={iconColor} fill={fill} style={isPill ? { color } : undefined} />}
        <span style={{ ...typography.body, fontWeight: 600, color, userSelect: "none", WebkitUserSelect: "none" }}>{displayCount ?? label}</span>
      </div>
    </AnimatedWrapper>
  );
}
